import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, CreditCard, ArrowRight } from "lucide-react";
import api from "@/lib/api";

/**
 * DashboardAlerts
 * ---------------
 * Renders page-level, high-priority operational alerts at the very top
 * of the Dashboard. Two categories today:
 *
 *   1. Reauth required — any connected platform where the returned
 *      `needs_reauth === true` (from the /api/connections payload passed
 *      in via props). Purely a display of existing state; the "Reconnect"
 *      button links to the existing /settings/connections flow — we do
 *      NOT initiate OAuth or mutate tokens from here.
 *
 *   2. Payment problem — surfaced when the existing subscription data
 *      reports a failed payment or a past_due status. Also a display of
 *      existing state; the "Fix billing" button links to /billing which
 *      already has the customer-portal redirect logic.
 *
 * cancel_at_period_end is intentionally NOT surfaced here — it's an
 * informational state, not an emergency, and PlanUsageWidget already
 * shows it inline.
 */
export default function DashboardAlerts({ connectionsPayload }) {
  const [sub, setSub] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/billing/subscription");
        if (!cancelled) setSub(res.data || null);
      } catch {
        // Silent — a billing fetch failure shouldn't block the whole
        // Dashboard from rendering.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Reauth: only for platforms that WERE connected (i.e. real token
  // existed) and now need reauth. A "Not connected" platform must not
  // masquerade as an emergency.
  const reauthPlatforms = (connectionsPayload?.platforms || []).filter(
    (p) => p && p.needs_reauth === true,
  );

  const paymentFailed = !!(
    sub && (
      sub.billing_status === "payment_failed" ||
      sub.billing_status === "final_notice" ||
      sub.last_payment_status === "failed" ||
      sub.status === "past_due"
    )
  );
  const isFinalNotice = sub?.billing_status === "final_notice";
  const graceDateStr = (() => {
    const iso = sub?.grace_period_ends_at;
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleDateString(undefined, {
        year: "numeric", month: "long", day: "numeric",
      });
    } catch (_) { return ""; }
  })();

  if (!reauthPlatforms.length && !paymentFailed) return null;

  return (
    <div className="space-y-2 mb-4" data-testid="dashboard-alerts">
      {paymentFailed ? (
        <div
          className="rounded-[16px] glass p-4 flex items-center justify-between flex-wrap gap-3 border border-[color:rgba(255,138,61,0.35)]"
          data-testid="dashboard-alert-payment"
          role="alert"
        >
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-md bg-[color:rgba(255,138,61,0.18)] flex items-center justify-center text-[color:var(--accent-warm)]">
              <CreditCard className="h-4 w-4" />
            </div>
            <div>
              <div className="font-display text-base font-semibold text-[color:var(--accent-warm)]">
                {isFinalNotice
                  ? "Final notice — your subscription will end soon"
                  : "Payment problem on your subscription"}
              </div>
              <div className="text-xs text-[color:var(--text-2)]">
                {isFinalNotice && graceDateStr
                  ? `Your Postmoro subscription will end on ${graceDateStr} unless your payment method is updated.`
                  : graceDateStr
                    ? `Payment issue — update your payment method by ${graceDateStr} to keep your Postmoro subscription active.`
                    : (sub?.status === "past_due"
                      ? "Your account is past due. Update your billing info to restore full access."
                      : "Your last payment did not go through. Update your card to keep Postmoro running smoothly.")}
              </div>
            </div>
          </div>
          <Link to="/billing">
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-md bg-[color:var(--accent-warm)] hover:opacity-90 text-[color:var(--bg-0)] font-semibold px-3.5 py-2 text-sm transition"
              data-testid="dashboard-alert-payment-cta"
            >
              Fix billing <ArrowRight className="h-4 w-4" />
            </button>
          </Link>
        </div>
      ) : null}

      {reauthPlatforms.map((p) => (
        <div
          key={p.platform}
          className="rounded-[16px] glass p-4 flex items-center justify-between flex-wrap gap-3 border border-[color:rgba(255,138,61,0.35)]"
          data-testid={`dashboard-alert-reauth-${(p.platform || "").toLowerCase()}`}
          role="alert"
        >
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-md bg-[color:rgba(255,138,61,0.18)] flex items-center justify-center text-[color:var(--accent-warm)]">
              <AlertTriangle className="h-4 w-4" />
            </div>
            <div>
              <div className="font-display text-base font-semibold text-[color:var(--accent-warm)]">
                {p.label || p.platform} needs to be reconnected
              </div>
              <div className="text-xs text-[color:var(--text-2)]">
                {p.status_message
                  || "Your access token has expired or been revoked. Reconnect to keep posting."}
              </div>
            </div>
          </div>
          <Link to="/settings/connections">
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-md bg-[color:var(--accent-warm)] hover:opacity-90 text-[color:var(--bg-0)] font-semibold px-3.5 py-2 text-sm transition"
              data-testid={`dashboard-alert-reauth-cta-${(p.platform || "").toLowerCase()}`}
            >
              Reconnect <ArrowRight className="h-4 w-4" />
            </button>
          </Link>
        </div>
      ))}
    </div>
  );
}
